import morph from "../helpers/morph";
import { initializeTooltips } from "../global/tooltip/tooltip";

/*
 * Hover dwell before a prefetch fires. A sweep across a swatch grid
 * should cost one request, not one per swatch.
 */
const PREFETCH_DWELL_MS = 120;

/*
 * Section renders cost seconds each, so a speculative request that is
 * no longer wanted is actively harmful — it holds a connection the
 * real click needs. Past this many, the oldest is aborted.
 */
const MAX_CONCURRENT_PREFETCH = 2;

class ProductAjaxNavigation {
  constructor() {
    this.sectionSelector = "[data-main-product-section]";
    this.selectorContainerSelector = "[data-product-form-selectors]";
    this.swatchSelector = "[data-product-swatch-link]";

    /*
     * Stores promises rather than only completed HTML.
     *
     * This means hover → click shares the same request instead
     * of starting another Shopify request.
     */
    this.sectionCache = new Map();

    this.activePrefetches =
      new Map();

    this.prefetchTimer = null;

    this.pendingPrefetchPath =
      null;

    /*
     * Latest click wins.
     *
     * If the user clicks:
     * Black → Pink → Blue
     *
     * we don't block Pink/Blue while Black is loading.
     */
    this.requestSequence = 0;

    this.defaultChildInitialized = false;

    this.handleDocumentClick =
      this.handleDocumentClick.bind(this);

    this.handlePopState =
      this.handlePopState.bind(this);

    this.handlePrefetchIntent =
      this.handlePrefetchIntent.bind(this);

    this.init();
  }

  init() {
    if (!document.querySelector(this.sectionSelector)) {
      return;
    }

    if (window.productAjaxNavigationInitialized) {
      return;
    }

    window.productAjaxNavigationInitialized = true;

    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }

    document.addEventListener(
      "click",
      this.handleDocumentClick,
    );

    window.addEventListener(
      "popstate",
      this.handlePopState,
    );

    /*
     * Desktop:
     * Start loading before click.
     */
    document.addEventListener(
      "pointerover",
      this.handlePrefetchIntent,
      {
        passive: true,
      },
    );

    /*
     * Keyboard users.
     */
    document.addEventListener(
      "focusin",
      this.handlePrefetchIntent,
      {
        passive: true,
      },
    );

    /*
     * Mobile:
     * Start request on touch rather than waiting
     * for the following click event.
     */
    document.addEventListener(
      "touchstart",
      this.handlePrefetchIntent,
      {
        passive: true,
      },
    );

    this.initializeDefaultChild();
  }

  initializeDefaultChild() {
    if (this.defaultChildInitialized) {
      return;
    }

    const selectorContainer =
      document.querySelector(
        this.selectorContainerSelector,
      );

    const defaultChildUrl =
      selectorContainer?.dataset.defaultChildUrl;

    if (!defaultChildUrl) {
      return;
    }

    const currentUrl =
      new URL(window.location.href);

    const destinationUrl =
      new URL(
        defaultChildUrl,
        window.location.origin,
      );

    const isAlreadyDefaultChild =
      this.normalizePath(
        currentUrl.pathname,
      ) ===
      this.normalizePath(
        destinationUrl.pathname,
      );

    if (isAlreadyDefaultChild) {
      return;
    }

    this.defaultChildInitialized = true;

    this.loadProduct(
      destinationUrl,
      {
        historyAction: "replace",
      },
    );
  }

  handlePrefetchIntent(event) {
    const link =
      event.target.closest?.(
        this.swatchSelector,
      );

    if (!link?.href) {
      return;
    }

    const destinationUrl =
      new URL(
        link.href,
        window.location.origin,
      );

    const currentUrl =
      new URL(window.location.href);

    if (
      this.urlsMatch(
        currentUrl,
        destinationUrl,
      )
    ) {
      return;
    }

    /*
     * Touch is a committed gesture, so it skips the dwell.
     */
    if (
      event.type === "touchstart"
    ) {
      this.prefetchProduct(
        destinationUrl,
      );

      return;
    }

    const destinationPath =
      destinationUrl.pathname;

    /*
     * pointerover repeats while the cursor sits on one swatch;
     * only a move to a different swatch restarts the dwell.
     */
    if (
      this.pendingPrefetchPath ===
      destinationPath
    ) {
      return;
    }

    window.clearTimeout(
      this.prefetchTimer,
    );

    this.pendingPrefetchPath =
      destinationPath;

    this.prefetchTimer =
      window.setTimeout(() => {
        this.pendingPrefetchPath =
          null;

        this.prefetchProduct(
          destinationUrl,
        );
      }, PREFETCH_DWELL_MS);
  }

  handleDocumentClick(event) {
    const link =
      event.target.closest(
        this.swatchSelector,
      );

    if (!link) {
      return;
    }

    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }

    const currentUrl =
      new URL(window.location.href);

    const destinationUrl =
      new URL(
        link.href,
        window.location.origin,
      );

    if (
      this.urlsMatch(
        currentUrl,
        destinationUrl,
      )
    ) {
      event.preventDefault();

      return;
    }

    event.preventDefault();

    window.clearTimeout(
      this.prefetchTimer,
    );

    this.pendingPrefetchPath = null;

    /*
     * Navigation begins immediately.
     */
    this.loadProduct(
      destinationUrl,
      {
        historyAction: "push",

        scrollToFormTop:
          link.hasAttribute(
            "data-scroll-to-form-top",
          ),
      },
    );
  }

  handlePopState() {
    this.loadProduct(
      new URL(window.location.href),
      {
        historyAction: "none",
      },
    );
  }

  normalizePath(pathname) {
    return (
      pathname.replace(/\/+$/, "") ||
      "/"
    );
  }

  urlsMatch(a, b) {
    return (
      this.normalizePath(a.pathname) ===
        this.normalizePath(b.pathname) &&
      a.search === b.search
    );
  }

  getSectionId() {
    return (
      document.querySelector(
        this.sectionSelector,
      )?.dataset.sectionId || null
    );
  }

  getCacheKey(
    destinationUrl,
    sectionId,
  ) {
    const url =
      new URL(destinationUrl.href);

    url.hash = "";

    url.searchParams.delete(
      "section_id",
    );

    return (
      `${sectionId}:` +
      `${url.pathname}` +
      `${url.search}`
    );
  }

  getSectionRequestUrl(
    destinationUrl,
    sectionId,
  ) {
    const url =
      new URL(destinationUrl.href);

    url.searchParams.set(
      "section_id",
      sectionId,
    );

    return url;
  }

  async fetchSectionHtml(
    destinationUrl,
    sectionId,
    signal = null,
  ) {
    const cacheKey =
      this.getCacheKey(
        destinationUrl,
        sectionId,
      );

    /*
     * A real navigation promotes the request out of the
     * speculative pool so a later hover can never abort it.
     */
    if (!signal) {
      this.activePrefetches.delete(
        cacheKey,
      );
    }

    /*
     * Could already be:
     *
     * - a hover-prefetch request in progress
     * - a completed cached product
     */
    if (
      this.sectionCache.has(cacheKey)
    ) {
      return this.sectionCache.get(
        cacheKey,
      );
    }

    const requestUrl =
      this.getSectionRequestUrl(
        destinationUrl,
        sectionId,
      );

    const request = fetch(
      requestUrl.href,
      {
        method: "GET",

        signal,

        headers: {
          Accept: "text/html",

          "X-Requested-With":
            "XMLHttpRequest",
        },
      },
    )
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(
            `Unable to load product section. Status: ${response.status}`,
          );
        }

        const html =
          await response.text();

        /*
         * Don't wait for this.
         *
         * Start browser/CDN image loading immediately
         * while user may still only be hovering.
         */
        this.warmCriticalMedia(html);

        return html;
      })
      .catch((error) => {
        /*
         * Failed requests shouldn't poison cache.
         */
        this.sectionCache.delete(
          cacheKey,
        );

        throw error;
      });

    /*
     * Store immediately.
     *
     * A click during hover-prefetch receives the
     * same Promise.
     */
    this.sectionCache.set(
      cacheKey,
      request,
    );

    return request;
  }

  /*
   * Drops the oldest speculative request once the cap is
   * reached. Its cache entry goes too, so a later click
   * re-requests rather than awaiting a promise that will
   * never settle.
   */
  evictOldestPrefetch() {
    while (
      this.activePrefetches.size >=
      MAX_CONCURRENT_PREFETCH
    ) {
      const [cacheKey, controller] =
        this.activePrefetches
          .entries()
          .next().value;

      this.activePrefetches.delete(
        cacheKey,
      );

      this.sectionCache.delete(
        cacheKey,
      );

      controller.abort();
    }
  }

  prefetchProduct(
    destinationUrl,
  ) {
    const sectionId =
      this.getSectionId();

    if (!sectionId) {
      return;
    }

    const cacheKey =
      this.getCacheKey(
        destinationUrl,
        sectionId,
      );

    if (
      this.sectionCache.has(cacheKey)
    ) {
      return;
    }

    this.evictOldestPrefetch();

    const controller =
      new AbortController();

    this.activePrefetches.set(
      cacheKey,
      controller,
    );

    this.fetchSectionHtml(
      destinationUrl,
      sectionId,
      controller.signal,
    )
      .catch(() => {
        /*
         * Prefetch failure does not affect
         * normal navigation.
         */
      })
      .finally(() => {
        this.activePrefetches.delete(
          cacheKey,
        );
      });
  }

  isRenderableProductSection(section) {
    const productForm =
      section.querySelector(
        "[data-page-product-id]",
      );

    if (
      productForm &&
      !productForm.dataset.pageProductId
    ) {
      return false;
    }

    return !section.textContent.includes(
      "Liquid error",
    );
  }

  parseSection(html) {
    const parsedDocument =
      new DOMParser().parseFromString(
        html,
        "text/html",
      );

    return parsedDocument.querySelector(
      this.sectionSelector,
    );
  }

  /*
   * Get only the images that can immediately
   * appear inside the viewport.
   *
   * Your desktop gallery exposes part of slide 2,
   * so warming two images prevents the secondary
   * image from popping in immediately afterwards.
   */
  getCriticalImages(section) {
    const gallery =
      section?.querySelector(
        "media-gallery",
      );

    if (!gallery) {
      return [];
    }

    const wrappers =
      Array.from(
        gallery.querySelectorAll(
          ".media-gallery__media-wrapper",
        ),
      );

    if (!wrappers.length) {
      return [];
    }

    const requestedIndex =
      Number(
        gallery.dataset
          .initialMediaIndex,
      );

    const initialIndex =
      Number.isInteger(
        requestedIndex,
      )
        ? Math.max(
            0,
            Math.min(
              requestedIndex,
              wrappers.length - 1,
            ),
          )
        : 0;

    const indexes = [
      initialIndex,
      initialIndex + 1,
    ].filter(
      (index) =>
        index < wrappers.length,
    );

    return indexes
      .map((index) =>
        wrappers[index]?.querySelector(
          "img",
        ),
      )
      .filter(Boolean);
  }

  /*
   * Thumbnails clamp to width=90/180, so the whole strip costs less
   * than a single gallery slide — no need for the two-image window
   * getCriticalImages uses.
   */
  getCriticalThumbnails(section) {
    const gallery =
      section?.querySelector(
        "media-gallery",
      );

    if (!gallery) {
      return [];
    }

    return Array.from(
      gallery.querySelectorAll(
        "[data-gallery-original-thumbnail] img",
      ),
    );
  }

  resolveImageCandidate(image) {
    const picture =
      image.closest("picture");

    const source =
      picture &&
      Array.from(
        picture.querySelectorAll(
          "source",
        ),
      ).find((candidate) => {
        const media =
          candidate.getAttribute(
            "media",
          );

        return (
          !media ||
          window.matchMedia(media)
            .matches
        );
      });

    return {
      srcset:
        source?.getAttribute(
          "srcset",
        ) ||
        image.getAttribute("srcset"),

      sizes:
        source?.getAttribute(
          "sizes",
        ) ||
        image.getAttribute("sizes"),

      src: source
        ? null
        : image.getAttribute("src"),
    };
  }

  preloadImageElement(image) {
    return new Promise(
      (resolve) => {
        const preload =
          new Image();

        const {
          src,
          srcset,
          sizes,
        } =
          this.resolveImageCandidate(
            image,
          );

        if (sizes) {
          preload.sizes =
            sizes;
        }

        if (srcset) {
          preload.srcset =
            srcset;
        }

        if (src) {
          preload.src =
            src;
        }

        const done =
          async () => {
            try {
              await preload.decode?.();
            } catch (_) {
              /*
               * decode() failure should never
               * block navigation.
               */
            }

            resolve();
          };

        if (preload.complete) {
          done();

          return;
        }

        preload.addEventListener(
          "load",
          done,
          {
            once: true,
          },
        );

        preload.addEventListener(
          "error",
          resolve,
          {
            once: true,
          },
        );
      },
    );
  }

  warmCriticalMedia(html) {
    try {
      const section =
        this.parseSection(html);

      if (!section) {
        return;
      }

      /*
       * During prefetch we have no reason
       * to use a timeout — just start the
       * browser loading these resources.
       */
      const images =
        this.getCriticalImages(
          section,
        );

      const slidesWarmed =
        Promise.all(
          images.map((image) =>
            this.preloadImageElement(
              image,
            ),
          ),
        );

      /*
       * Thumbnails follow the slides rather than racing them, so
       * speculative hover traffic never competes with the image the
       * eye lands on first. preloadImageElement resolves on error
       * too, so a failed slide still releases them.
       */
      slidesWarmed.then(() => {
        this.getCriticalThumbnails(
          section,
        ).forEach((thumbnail) => {
          this.preloadImageElement(
            thumbnail,
          );
        });
      });
    } catch (_) {
      /*
       * Performance enhancement only.
       */
    }
  }

  updateBrowserHistory(
    destinationUrl,
    historyAction,
  ) {
    if (
      historyAction === "none"
    ) {
      return;
    }

    const cleanUrl =
      new URL(destinationUrl.href);

    cleanUrl.searchParams.delete(
      "section_id",
    );

    const state = {
      productUrl:
        cleanUrl.href,
    };

    const browserUrl =
      `${cleanUrl.pathname}` +
      `${cleanUrl.search}` +
      `${cleanUrl.hash}`;

    if (
      historyAction === "replace"
    ) {
      window.history.replaceState(
        state,
        "",
        browserUrl,
      );

      return;
    }

    window.history.pushState(
      state,
      "",
      browserUrl,
    );
  }

  updateDocumentTitle(
    section,
  ) {
    const documentTitle =
      section.dataset
        .documentTitle;

    if (documentTitle) {
      document.title =
        documentTitle;
    }
  }

  dispatchRenderedEvent(
    section,
    destinationUrl,
  ) {
    document.dispatchEvent(
      new CustomEvent(
        "product:ajax-rendered",
        {
          detail: {
            url:
              destinationUrl.href,

            section,
          },
        },
      ),
    );
  }

  restoreScrollPosition(
    scrollY,
  ) {
    window.scrollTo({
      top: scrollY,
      left: 0,
      behavior: "auto",
    });
  }

  scrollToFormTop(section) {
    const target =
      section.querySelector(
        ".main-product__details-wrapper",
      ) || section;

    const rootStyle =
      getComputedStyle(
        document.documentElement,
      );

    const headerHeight =
      parseFloat(
        rootStyle.getPropertyValue(
          "--header-height",
        ),
      ) || 0;

    const promoBarHeight =
      parseFloat(
        rootStyle.getPropertyValue(
          "--promo-bar-height",
        ),
      ) || 0;

    const targetTop =
      target.getBoundingClientRect().top +
      window.scrollY -
      headerHeight -
      promoBarHeight -
      16;

    window.scrollTo({
      top:
        Math.max(
          targetTop,
          0,
        ),

      left: 0,

      behavior: "smooth",
    });
  }

  async loadProduct(
    destinationUrl,
    {
      historyAction = "push",
      scrollToFormTop = false,
    } = {},
  ) {
    const currentSection =
      document.querySelector(
        this.sectionSelector,
      );

    if (!currentSection) {
      return;
    }

    const sectionId =
      currentSection.dataset
        .sectionId;

    if (!sectionId) {
      window.location.href =
        destinationUrl.href;

      return;
    }

    /*
     * The latest navigation owns the page.
     *
     * Do NOT disable swatches while loading.
     */
    const requestId =
      ++this.requestSequence;

    const savedScrollY =
      window.scrollY;

    /*
     * Address bar changes immediately.
     */
    this.updateBrowserHistory(
      destinationUrl,
      historyAction,
    );

    currentSection.setAttribute(
      "aria-busy",
      "true",
    );

    try {
      /*
       * Usually this request will already exist because
       * pointerover/touchstart started it.
       */
      const html =
        await this.fetchSectionHtml(
          destinationUrl,
          sectionId,
        );

      /*
       * User has clicked another product while
       * this one was loading.
       */
      if (
        requestId !==
        this.requestSequence
      ) {
        return;
      }

      const newSection =
        this.parseSection(html);

      if (!newSection) {
        throw new Error(
          "Updated product section was not found.",
        );
      }

      /*
       * Shopify can answer 200 with the section rendered but no
       * `product` (e.g. the URL redirects to a non-product page, or the
       * product isn't renderable in this section), which shows up as
       * "Liquid error ... product form must be given a product".
       * Never morph that in; the full-page fallback below loads it properly.
       */
      if (!this.isRenderableProductSection(newSection)) {
        throw new Error(
          "Updated product section was rendered without a product.",
        );
      }

      morph(
        currentSection,
        newSection,
      );

      const updatedSection =
        document.querySelector(
          this.sectionSelector,
        );

      if (!updatedSection) {
        throw new Error(
          "Updated product section was not found after morph.",
        );
      }

      initializeTooltips(
        updatedSection,
      );

      this.updateDocumentTitle(
        updatedSection,
      );

      updatedSection.removeAttribute(
        "aria-busy",
      );

      if (scrollToFormTop) {
        this.scrollToFormTop(
          updatedSection,
        );
      } else {
        this.restoreScrollPosition(
          savedScrollY,
        );
      }

      this.dispatchRenderedEvent(
        updatedSection,
        destinationUrl,
      );

      /*
       * Don't make the customer wait for sections
       * outside the primary PDP.
       */
      this.refreshReactiveSections(
        destinationUrl,
      );
    } catch (error) {
      /*
       * Ignore errors from an older/stale request.
       */
      if (
        requestId !==
        this.requestSequence
      ) {
        return;
      }

      console.error(
        "Product AJAX navigation failed:",
        error,
      );

      /*
       * Safety fallback.
       */
      window.location.href =
        destinationUrl.href;
    } finally {
      if (
        requestId !==
        this.requestSequence
      ) {
        return;
      }

      document
        .querySelector(
          this.sectionSelector,
        )
        ?.removeAttribute(
          "aria-busy",
        );
    }
  }

  getReactiveSections() {
    return Array.from(
      document.querySelectorAll(
        "[data-ajax-reactive-section]",
      ),
    ).filter(
      (element) =>
        !element.closest(
          this.sectionSelector,
        ),
    );
  }

  async refreshReactiveSections(
    destinationUrl,
  ) {
    const reactiveSections =
      this.getReactiveSections();

    if (
      !reactiveSections.length
    ) {
      return;
    }

    await Promise.all(
      reactiveSections.map(
        (section) =>
          this.refreshReactiveSection(
            section,
            destinationUrl,
          ),
      ),
    );
  }

  async refreshReactiveSection(
    currentEl,
    destinationUrl,
  ) {
    const sectionId =
      currentEl.dataset
        .sectionId;

    if (!sectionId) {
      return;
    }

    try {
      const requestUrl =
        new URL(
          destinationUrl.href,
        );

      requestUrl.searchParams.set(
        "section_id",
        sectionId,
      );

      const response =
        await fetch(
          requestUrl.href,
          {
            method: "GET",

            headers: {
              Accept:
                "text/html",

              "X-Requested-With":
                "XMLHttpRequest",
            },
          },
        );

      if (!response.ok) {
        return;
      }

      const html =
        await response.text();

      const parsedDocument =
        new DOMParser().parseFromString(
          html,
          "text/html",
        );

      const newEl =
        parsedDocument.querySelector(
          `[data-ajax-reactive-section][data-section-id="${sectionId}"]`,
        );

      if (!newEl) {
        currentEl.remove();

        return;
      }

      morph(
        currentEl,
        newEl,
      );
    } catch (error) {
      console.error(
        `Failed to refresh reactive section "${sectionId}":`,
        error,
      );
    }
  }
}

new ProductAjaxNavigation();